import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './Controls.css';
import axiosInstance from '../api';

function ControlsPage() {
  const { t } = useTranslation();
  const [controls, setControls]               = useState([]);
  const [frameworks, setFrameworks]           = useState([]);
  const [myFrameworks, setMyFrameworks]       = useState([]); // enrolled frameworks (custom-control form)
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [currentPage, setCurrentPage]         = useState(1);
  const [perPage, setPerPage]                 = useState(10);
  const [totalControls, setTotalControls]     = useState(0);
  const [totalPages, setTotalPages]           = useState(1);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [expandedRows, setExpandedRows]       = useState({});
  const [isAdding, setIsAdding]               = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [newControl, setNewControl]           = useState({ name: '', description: '', frameworkId: '', policyIds: [], evidenceCadence: 'ANNUAL' });
  const [orgPolicies, setOrgPolicies]         = useState([]); // the org's adopted policies (link picker)

  const role = localStorage.getItem('role');
  const orgRole = localStorage.getItem('orgRole');
  const isCpa = orgRole === 'CPA_ADMIN' || orgRole === 'CISO_RESELLER';
  const [authorMode, setAuthorMode] = useState('custom'); // 'custom' | 'partner'
  const [partnerAvailable, setPartnerAvailable] = useState([]); // adoptable partner-library controls

  const fetchFrameworks = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/frameworks');
      setFrameworks(res.data || []);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // The org's adopted policies, offered as optional governance links on a custom control.
  const fetchOrgPolicies = useCallback(async () => {
    if (role === 'ROLE_PLATFORM_ADMIN') return;
    try {
      const res = await axiosInstance.get('/organization-policies');
      setOrgPolicies((res.data || []).map(p => ({ id: p.id, name: p.policyName || p.policyDescription || ('Policy #' + p.id) })));
    } catch { /* non-fatal */ }
  }, [role]);

  // A customer can only author a control against a framework they are enrolled in.
  const fetchMyFrameworks = useCallback(async () => {
    if (role === 'ROLE_PLATFORM_ADMIN') return;
    try {
      const res = await axiosInstance.get('/compliance/frameworks');
      // /compliance/frameworks returns every framework with coverage stats but no explicit
      // 'enrolled' flag; an enrolled framework is the one that actually has seeded controls
      // (totalControls > 0). A custom control can only attach to a framework the org is enrolled in.
      const list = (res.data || [])
        .filter(f => (f.totalControls ?? 0) > 0)
        .map(f => ({ id: f.frameworkId ?? f.id, displayName: f.frameworkName ?? f.displayName ?? f.name }));
      setMyFrameworks(list);
    } catch { /* non-fatal: form falls back to empty and the server still validates enrolment */ }
  }, [role]);

  const fetchControls = useCallback(async () => {
    try {
      setLoading(true);
      // Platform admin sees the global control catalog; a customer org-admin (who also holds
      // ROLE_ADMIN authority) must see their OWN org-scoped controls -> key off ROLE_PLATFORM_ADMIN.
      const url = role === 'ROLE_PLATFORM_ADMIN' ? '/requirement-subcategory/controls' : '/organization-control';
      const params = {
        page: currentPage - 1,
        size: perPage,
        // Server binds different param names per role, both matching the
        // framework display NAME (not id): org path -> frameWork
        // (OrganizationControlsSearchRequestFilters), admin -> framework
        // (ControlsSearchRequestFilters).
        ...(selectedFramework !== 'all' && (role === 'ROLE_PLATFORM_ADMIN'
          ? { framework: selectedFramework }
          : { frameWork: selectedFramework })),
      };
      const res = await axiosInstance.get(url, { params });
      let items = [];
      if (res.data?.content) {
        items = res.data.content;
        const total = res.data.totalElements || items.length;
        setTotalControls(total);
        setTotalPages(Math.ceil(total / perPage));
      } else if (Array.isArray(res.data)) {
        items = res.data;
        const totalFromHeader = parseInt(res.headers['x-total-count'] || '0', 10);
        const total = totalFromHeader > 0 ? totalFromHeader : items.length;
        setTotalControls(total);
        setTotalPages(Math.ceil(total / perPage));
      }
      setControls(items);
      setError(null);
    } catch (err) {
      setError(err.message);
      setControls([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, selectedFramework, role]);

  useEffect(() => { fetchFrameworks(); }, [fetchFrameworks]);
  useEffect(() => { fetchMyFrameworks(); }, [fetchMyFrameworks]);
  const fetchPartnerAvailable = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/partner-library/controls/available');
      setPartnerAvailable(res.data || []);
    } catch { /* no partner / non-fatal */ }
  }, []);
  useEffect(() => { fetchOrgPolicies(); }, [fetchOrgPolicies]);
  useEffect(() => { fetchPartnerAvailable(); }, [fetchPartnerAvailable]);
  useEffect(() => { fetchControls(); }, [fetchControls]);

  const saveControl = async () => {
    if (!newControl.name.trim()) {
      alert(t('Controls.validationNameRequired'));
      return;
    }
    if (role !== 'ROLE_PLATFORM_ADMIN' && !newControl.frameworkId) {
      alert(t('Controls.validationFrameworkRequired'));
      return;
    }
    try {
      if (authorMode === 'partner') {
        await axiosInstance.post('/partner-library/controls', {
          name: newControl.name,
          description: newControl.description,
          frameworkId: Number(newControl.frameworkId),
        });
      } else if (role === 'ROLE_PLATFORM_ADMIN') {
        await axiosInstance.post('/controls', newControl); // global catalog control
      } else {
        await axiosInstance.post('/organization-control/custom', {
          name: newControl.name,
          description: newControl.description,
          frameworkId: Number(newControl.frameworkId),
          policyIds: newControl.policyIds || [],
          evidenceCadence: newControl.evidenceCadence || 'ANNUAL',
        });
      }
      setIsAdding(false); setAuthorMode('custom');
      setNewControl({ name: '', description: '', frameworkId: '', policyIds: [], evidenceCadence: 'ANNUAL' });
      fetchControls();
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data || t('Controls.toastSaveFailed'));
    }
  };

  const adoptPartnerControl = async (control) => {
    try {
      await axiosInstance.post(`/partner-library/controls/${control.id}/adopt`);
      fetchControls();
      fetchPartnerAvailable();
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data || t('Controls.adoptFailed', 'Could not adopt this control.'));
    }
  };

  const deleteCustomControl = async (control) => {
    if (!control?.orgControlId) return;
    if (!window.confirm(t('Controls.confirmDeleteCustom', { name: control.name }))) return;
    try {
      await axiosInstance.delete(`/organization-control/custom/${control.orgControlId}`);
      fetchControls();
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data || t('Controls.toastDeleteFailed'));
    }
  };

  const toggleExpand = (key) => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fwLabel = (fw) => (typeof fw === 'object' ? fw.displayName || fw.name || fw.id : fw);

  const displayed = controls.filter(c =>
    !searchQuery ||
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.key?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isAdding) {
    return (
      <div className="co-page">
        <div className="co-form-card">
          <div className="co-form-header">
            <span>{authorMode === 'partner' ? t('Controls.authorPartnerControl', 'Author partner-library control') : (role === 'ROLE_PLATFORM_ADMIN' ? t('Controls.addControl') : t('Controls.addCustomControl'))}</span>
            <button className="co-form-close" onClick={() => { setIsAdding(false); setAuthorMode('custom'); setNewControl({ name: '', description: '', frameworkId: '', policyIds: [], evidenceCadence: 'ANNUAL' }); }}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="co-form-body">
            <div className="co-field">
              <label>{t('Controls.controlNameLabel')}</label>
              <input
                className="co-input"
                placeholder={t('Controls.controlNamePlaceholder')}
                value={newControl.name}
                onChange={e => setNewControl({ ...newControl, name: e.target.value })}
              />
            </div>
            <div className="co-field">
              <label>{t('Controls.descriptionLabel')}</label>
              <textarea
                className="co-input co-textarea"
                placeholder={t('Controls.descriptionPlaceholder')}
                rows={3}
                value={newControl.description}
                onChange={e => setNewControl({ ...newControl, description: e.target.value })}
              />
            </div>
            <div className="co-field">
              <label>{t('Controls.frameworkLabel')}</label>
              <select
                className="co-input"
                value={newControl.frameworkId}
                onChange={e => setNewControl({ ...newControl, frameworkId: e.target.value })}
              >
                <option value="">{t('Controls.selectFramework')}</option>
                {(role === 'ROLE_PLATFORM_ADMIN' || authorMode === 'partner' ? frameworks : myFrameworks).map(fw => (
                  <option key={fw.id} value={fw.id}>{fwLabel(fw)}</option>
                ))}
              </select>
            </div>
            {role !== 'ROLE_PLATFORM_ADMIN' && (
              <>
                <div className="co-field">
                  <label>{t('Controls.linkPoliciesLabel')}</label>
                  <div className="co-policy-picker">
                    {orgPolicies.length === 0 ? (
                      <p className="co-policy-empty">{t('Controls.noPoliciesToLink')}</p>
                    ) : orgPolicies.map(pol => (
                      <label key={pol.id} className="co-policy-item">
                        <input type="checkbox"
                          checked={(newControl.policyIds || []).includes(pol.id)}
                          onChange={e => setNewControl(nc => ({
                            ...nc,
                            policyIds: e.target.checked
                              ? [...(nc.policyIds || []), pol.id]
                              : (nc.policyIds || []).filter(id => id !== pol.id),
                          }))} />
                        <span>{pol.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="co-field-hint">{t('Controls.linkPoliciesHint')}</p>
                </div>
                <div className="co-field">
                  <label>{t('Controls.evidenceCadenceLabel')}</label>
                  <select className="co-input" value={newControl.evidenceCadence}
                    onChange={e => setNewControl(nc => ({ ...nc, evidenceCadence: e.target.value }))}>
                    <option value="ANNUAL">{t('Controls.cadenceAnnual')}</option>
                    <option value="QUARTERLY">{t('Controls.cadenceQuarterly')}</option>
                  </select>
                </div>
                <div className="co-evidence-note">
                  <i className="fas fa-circle-info" /> {t('Controls.manualTestNote')}
                </div>
              </>
            )}
          </div>
          <div className="co-form-footer">
            <button className="co-btn-secondary" onClick={() => setIsAdding(false)}>{t('Controls.cancel')}</button>
            <button className="co-btn-primary" onClick={saveControl}>{t('Controls.saveControl')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      <div className="co-card">
        {partnerAvailable && partnerAvailable.length > 0 && (
          <div className="co-partner-avail">
            <div className="co-partner-avail-hd">
              <i className="fas fa-book" /> {t('Controls.availableFromPartner', 'Available from your compliance partner')}
              <span className="co-partner-avail-count">{partnerAvailable.filter(c => !c.custom).length}</span>
            </div>
            <div className="co-partner-avail-list">
              {partnerAvailable.map(c => (
                <div key={c.id} className="co-partner-avail-item">
                  <div className="co-partner-avail-meta">
                    <span className="co-partner-avail-name">{c.name}</span>
                    {c.description && <span className="co-partner-avail-desc">{c.description}</span>}
                  </div>
                  {c.custom
                    ? <span className="co-partner-adopted"><i className="fas fa-check" /> {t('Controls.adopted', 'Adopted')}</span>
                    : <button className="co-btn-primary" style={{ padding: '5px 14px', fontSize: 12 }} onClick={() => adoptPartnerControl(c)}>
                        <i className="fas fa-plus" /> {t('Controls.adopt', 'Adopt')}
                      </button>}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Toolbar */}
        <div className="co-toolbar">
          <div className="co-search-wrap">
            <i className="fas fa-search co-search-icon" />
            <input
              type="text"
              className="co-search"
              placeholder={t('Controls.searchPlaceholder')}
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="co-filter-wrap">
            <i className="fas fa-filter co-filter-icon" />
            <select
              className="co-select"
              value={selectedFramework}
              onChange={e => { setSelectedFramework(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">{t('Controls.allFrameworks')}</option>
              {frameworks.map(fw => (
                <option key={fw.id} value={fw.displayName || fw.name}>{fwLabel(fw)}</option>
              ))}
            </select>
          </div>
          {(role === 'ROLE_PLATFORM_ADMIN' || role === 'ROLE_ADMIN') && (
            <button className="co-add-btn" onClick={() => setIsAdding(true)}>
              <i className="fas fa-plus" /> {role === 'ROLE_PLATFORM_ADMIN' ? t('Controls.addControl') : t('Controls.addCustomControl')}
            </button>
          )}
          {isCpa && (
            <button className="co-add-btn" style={{ background: '#6b21a8' }}
              onClick={() => { setAuthorMode('partner'); setNewControl({ name: '', description: '', frameworkId: '', policyIds: [], evidenceCadence: 'ANNUAL' }); setIsAdding(true); }}>
              <i className="fas fa-book" /> {t('Controls.authorPartnerControl', 'Author library control')}
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="co-loading"><div className="co-spinner" /></div>
        ) : error ? (
          <div className="co-error">{t('Controls.errorPrefix', { error })}</div>
        ) : (
          <div className="co-table-wrap">
            <table className="co-table">
              <thead>
                <tr>
                  <th>{t('Controls.colKey')}</th>
                  <th>{t('Controls.colName')}</th>
                  <th>{t('Controls.colFramework')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="co-empty">
                      {searchQuery ? t('Controls.emptySearch') : t('Controls.emptyNoControls')}
                    </td>
                  </tr>
                ) : (
                  displayed.map((control, i) => {
                    const rowKey = control.key || i;
                    const isOpen = !!expandedRows[rowKey];
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className={`co-row ${isOpen ? 'co-row-open' : ''}`}>
                          <td className="co-key">{control.key || '—'}</td>
                          <td className="co-name">{control.name || '—'}{control.custom && <span className="co-custom-badge">{t('Controls.customBadge')}</span>}</td>
                          <td>
                            {control.framework && (
                              <div className="co-fw-wrap">
                                {Array.isArray(control.framework)
                                  ? control.framework.map((fw, j) => (
                                      <span key={j} className="co-fw-badge">{fwLabel(fw)}</span>
                                    ))
                                  : <span className="co-fw-badge">{fwLabel(control.framework)}</span>
                                }
                              </div>
                            )}
                          </td>
                          <td className="co-actions">
                            <button
                              className="co-view-btn"
                              onClick={() => toggleExpand(rowKey)}
                            >
                              {isOpen ? t('Controls.hide') : t('Controls.view')}
                              <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
                            </button>
                            {control.custom && (
                              <button className="co-del-btn" title={t('Controls.deleteCustom')} onClick={() => deleteCustomControl(control)}>
                                <i className="fas fa-trash" />
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="co-detail-row">
                            <td colSpan={4}>
                              <div className="co-detail-grid">
                                <div className="co-detail-block">
                                  <p className="co-detail-label">{t('Controls.detailControlId')}</p>
                                  <p className="co-detail-val">{control.key || '—'}</p>
                                </div>
                                <div className="co-detail-block co-desc-block">
                                  <p className="co-detail-label">{t('Controls.detailDescription')}</p>
                                  <p className="co-detail-val">{control.description || t('Controls.noDescription')}</p>
                                </div>
                                <div className="co-detail-block">
                                  <p className="co-detail-label">{t('Controls.detailCategory')}</p>
                                  <p className="co-detail-val">{control.category || '—'}</p>
                                </div>
                                <div className="co-detail-block">
                                  <p className="co-detail-label">{t('Controls.detailVersion')}</p>
                                  <p className="co-detail-val">{control.version || '—'}</p>
                                </div>
                                {control.custom && (
                                  <>
                                    <div className="co-detail-block">
                                      <p className="co-detail-label">{t('Controls.detailPolicies')}</p>
                                      <p className="co-detail-val">{control.linkedPolicies && control.linkedPolicies.length ? control.linkedPolicies.join(', ') : t('Controls.noneLinked')}</p>
                                    </div>
                                    <div className="co-detail-block co-desc-block">
                                      <p className="co-detail-label">{t('Controls.detailEvidenceTest')}</p>
                                      <p className="co-detail-val">
                                        {control.manualTestName || '—'}
                                        {control.manualTestName && <> &mdash; <a href="#/dashboard/evidence">{t('Controls.goToEvidence')}</a></>}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalControls > 0 && (
          <div className="co-pagination">
            <span className="co-page-info">
              {t('Controls.pageInfo', { from: (currentPage - 1) * perPage + 1, to: Math.min(currentPage * perPage, totalControls), total: totalControls })}
            </span>
            <div className="co-page-controls">
              <button className="co-page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                <i className="fas fa-angle-double-left" />
              </button>
              <button className="co-page-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                <i className="fas fa-chevron-left" />
              </button>
              <span className="co-page-num">{t('Controls.pageNum', { current: currentPage, total: totalPages })}</span>
              <button className="co-page-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>
                <i className="fas fa-chevron-right" />
              </button>
              <button className="co-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
                <i className="fas fa-angle-double-right" />
              </button>
              <select
                className="co-per-page"
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[5, 10, 25, 50].map(n => <option key={n} value={n}>{t('Controls.perPage', { count: n })}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ControlsPage;
